import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateReward } from "@factory/query/reward.query";
import { RewardFormRule, RewardFormSchema } from "@factory/form/form.rule";
import { rewardFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewReward() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreateReward } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Reward is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addReward, isLoading: isCreating } =
    useQyCreateReward(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<RewardFormSchema>({
    defaultValues: rewardFormDefault,
    resolver: zodResolver(RewardFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: RewardFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreateReward(updatedForm);
    addReward(formData);
  };
  const handleValidateError = (err: FieldErrors<RewardFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
